import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Flex } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import TreasurySidebar from "~/pages/Treasury/Sidebar";
import { User } from "~/types/user";
import {
  getRedirectPath,
  hasTreasuryAccessOnAnyArena,
  hasTreasuryConfigAccess,
  isTreasuryAdmin,
  isTreasuryUser,
} from "~/utils/userRoles";
import { TREASURY_ROUTES, CONFIGURATION_ROUTES } from "./routes";
import * as styles from "./styles_vanilla.css";
import { TreasuryEmptyState } from "./TreasuryEmptyState";

interface Props {
  currentUser: User;
}

const Treasury: React.FC<Props> = ({ currentUser }) => {
  const clientLevelPermissionsEnabled = useFeatureFlag("treasuryClientLevelPermissions", false);

  const hasTreasuryUserRole = clientLevelPermissionsEnabled
    ? hasTreasuryAccessOnAnyArena(currentUser.roles)
    : isTreasuryUser(currentUser.roles);
  const hasConfigAccess = clientLevelPermissionsEnabled
    ? isTreasuryAdmin(currentUser.roles) || hasTreasuryConfigAccess(currentUser.roles)
    : hasTreasuryConfigAccess(currentUser.roles);

  if (!hasTreasuryUserRole && !hasConfigAccess) {
    // With client-level permissions on, an unprovisioned user gets guidance to
    // request workspace access instead of a silent redirect.
    return clientLevelPermissionsEnabled ? (
      <TreasuryEmptyState />
    ) : (
      <Redirect to={getRedirectPath(currentUser)} />
    );
  }

  return (
    <Flex data-testid="treasury-root" className={styles.wrapperContainer}>
      <div className={styles.layoutContainer}>
        <TreasurySidebar />
        <Switch>
          {hasConfigAccess &&
            CONFIGURATION_ROUTES.map(({ path, component: Component }) => (
              <Route key={String(path)} path={path}>
                <Component />
              </Route>
            ))}
          {hasTreasuryUserRole &&
            TREASURY_ROUTES.map(({ path, component: Component }) => (
              <Route key={String(path)} path={path}>
                <Component />
              </Route>
            ))}
        </Switch>
      </div>
    </Flex>
  );
};

export default Treasury;
